import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowDown, ArrowUp, ArrowUpDown, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Club, ManagedUser, Permission, PermissionKey, Role } from "@/types";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card, Badge } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { ContactButtons } from "@/components/ContactButtons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";

interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const roleLabels: Record<Role, string> = {
  SUPER_ADMIN: "Super-admin",
  PROVIDER: "Proveedor",
  CLUB: "Usuario de club",
  REFEREE: "Árbitro",
};

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

const userSchema = z.object({
  nombre: z.string().min(2, "Mínimo 2 caracteres"),
  apellidos: z.string().max(80, "Máximo 80 caracteres").optional(),
  email: z.string().email("Email inválido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
  role: z.enum(["SUPER_ADMIN", "PROVIDER", "CLUB", "REFEREE"]),
  clubId: z.number().optional(),
  permissions: z.array(z.string()).optional(),
});
type UserForm = z.infer<typeof userSchema>;

type SortKey = "nombre" | "apellidos" | "role" | "active";

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink-700 ${
        active ? "text-ink-700" : ""
      }`}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function UsersPage() {
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
  const isProvider = currentUser?.role === "PROVIDER";
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [confirmUser, setConfirmUser] = useState<ManagedUser | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "nombre", dir: "asc" });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [nombreFilter, setNombreFilter] = useState("");
  const [apellidosFilter, setApellidosFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | "">("");
  const [clubIdFilter, setClubIdFilter] = useState<number | "">("");
  const [activeFilter, setActiveFilter] = useState<"all" | "true" | "false">("all");

  function goToFirstPage() {
    setPage(1);
  }

  const hasActiveFilters =
    Boolean(nombreFilter.trim()) ||
    Boolean(apellidosFilter.trim()) ||
    roleFilter !== "" ||
    clubIdFilter !== "" ||
    activeFilter !== "all";

  const { data: usersResp, isLoading } = useQuery({
    queryKey: ["users", { page, limit, nombreFilter, apellidosFilter, roleFilter, clubIdFilter, activeFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (nombreFilter.trim()) params.set("nombre", nombreFilter.trim());
      if (apellidosFilter.trim()) params.set("apellidos", apellidosFilter.trim());
      if (roleFilter) params.set("role", roleFilter);
      if (clubIdFilter !== "") params.set("clubId", String(clubIdFilter));
      if (activeFilter !== "all") params.set("active", activeFilter);
      return (await api.get<PaginatedResponse<ManagedUser>>(`/users?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const { data: clubs } = useQuery({
    queryKey: ["clubs"],
    queryFn: async () => (await api.get<{ data: Club[] }>("/clubs?limit=100")).data.data,
    enabled: isSuperAdmin || isProvider,
  });

  const { data: permissions } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => (await api.get<Permission[]>("/permissions")).data,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UserForm>({
    resolver: zodResolver(userSchema),
    defaultValues: { role: "CLUB", clubId: currentUser?.clubId ?? undefined, permissions: [], apellidos: "" },
  });

  const role = watch("role");
  const selectedPermissions = watch("permissions") ?? [];
  const showPermissions = !isSuperAdmin || role === "CLUB";

  const createUser = useMutation({
    mutationFn: (data: UserForm) => api.post("/users", data),
    onSuccess: () => {
      reset({
        role: "CLUB",
        clubId: currentUser?.clubId ?? undefined,
        permissions: [],
        nombre: "",
        apellidos: "",
        email: "",
        password: "",
      });
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuario creado correctamente");
    },
    onError: () => toast.error("No se pudo crear el usuario. Revisa los datos."),
  });

  const toggleActivo = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => api.put(`/users/${id}`, { active }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(vars.active ? "Usuario activado" : "Usuario desactivado");
    },
    onError: () => toast.error("No se pudo actualizar el usuario"),
  });

  function togglePermission(key: PermissionKey) {
    setValue(
      "permissions",
      selectedPermissions.includes(key)
        ? selectedPermissions.filter((p) => p !== key)
        : [...selectedPermissions, key]
    );
  }

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  // El servidor ya devuelve la página filtrada; esto solo reordena las filas de la página actual
  // según la columna en la que se haya pulsado (no vuelve a pedir datos al backend).
  const sortedUsers = useMemo(() => {
    const rows = usersResp?.data ?? [];
    const dirMult = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === "nombre") return a.nombre.localeCompare(b.nombre) * dirMult;
      if (sort.key === "apellidos") return (a.apellidos ?? "").localeCompare(b.apellidos ?? "") * dirMult;
      if (sort.key === "role") return a.role.localeCompare(b.role) * dirMult;
      return (Number(a.active) - Number(b.active)) * dirMult;
    });
  }, [usersResp, sort]);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Usuarios" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
            <Users className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Usuarios</h1>
            <p className="text-ink-400">
              {isSuperAdmin
                ? "Proveedores y usuarios de club de toda la plataforma."
                : isProvider
                  ? "Usuarios de tus clubes."
                  : "Usuarios de tu club."}
            </p>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Input
            id="filtro-nombre"
            label="Nombre"
            placeholder="Filtrar por nombre…"
            value={nombreFilter}
            onChange={(e) => {
              setNombreFilter(e.target.value);
              goToFirstPage();
            }}
          />
        </div>
        <div className="w-44">
          <Input
            id="filtro-apellidos"
            label="Apellidos"
            placeholder="Filtrar por apellidos…"
            value={apellidosFilter}
            onChange={(e) => {
              setApellidosFilter(e.target.value);
              goToFirstPage();
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-600">Rol</label>
          <select
            className={selectClass}
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as Role | "");
              goToFirstPage();
            }}
          >
            <option value="">Todos los roles</option>
            {(Object.keys(roleLabels) as Role[]).map((r) => (
              <option key={r} value={r}>
                {roleLabels[r]}
              </option>
            ))}
          </select>
        </div>
        {(isSuperAdmin || isProvider) && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Club</label>
            <select
              className={selectClass}
              value={clubIdFilter}
              onChange={(e) => {
                setClubIdFilter(e.target.value ? Number(e.target.value) : "");
                goToFirstPage();
              }}
            >
              <option value="">Todos los clubes</option>
              {clubs?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-600">Estado</label>
          <select
            className={selectClass}
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value as "all" | "true" | "false");
              goToFirstPage();
            }}
          >
            <option value="all">Todos</option>
            <option value="true">Activo</option>
            <option value="false">Inactivo</option>
          </select>
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={4} cols={6} />
        ) : !sortedUsers.length ? (
          <p className="p-6 text-sm text-ink-400">
            {hasActiveFilters ? "Ningún usuario coincide con los filtros." : "Todavía no hay usuarios."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                <tr>
                  <th className="px-6 py-3">
                    <SortHeader label="Nombre" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                  </th>
                  <th className="px-6 py-3">
                    <SortHeader label="Apellidos" active={sort.key === "apellidos"} dir={sort.dir} onClick={() => toggleSort("apellidos")} />
                  </th>
                  <th className="px-6 py-3">
                    <SortHeader label="Rol" active={sort.key === "role"} dir={sort.dir} onClick={() => toggleSort("role")} />
                  </th>
                  <th className="px-6 py-3 font-semibold">Permisos</th>
                  <th className="px-6 py-3">
                    <SortHeader label="Estado" active={sort.key === "active"} dir={sort.dir} onClick={() => toggleSort("active")} />
                  </th>
                  <th className="px-6 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50/50">
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-100 text-xs font-semibold text-ink-600">
                          {u.nombre.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <p className="font-medium text-ink-800">{u.nombre}</p>
                          <p className="text-xs text-ink-400">{u.email}</p>
                        </div>
                        <ContactButtons telefono={u.telefono} email={u.email} />
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-ink-600">{u.apellidos || <span className="text-ink-300">—</span>}</td>
                    <td className="px-6 py-3.5 text-ink-500">{roleLabels[u.role]}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-wrap gap-1">
                        {u.permissions.length ? u.permissions.map((p) => <Badge key={p}>{p}</Badge>) : <span className="text-ink-300">—</span>}
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <Badge tone={u.active ? "green" : "red"}>{u.active ? "Activo" : "Inactivo"}</Badge>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <Button className="w-28" onClick={() => setConfirmUser(u)}>
                        {u.active ? "Desactivar" : "Activar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {usersResp && (
        <Pagination
          page={usersResp.page}
          totalPages={usersResp.totalPages}
          total={usersResp.total}
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}

      <Modal
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) reset();
        }}
        title="Nuevo usuario"
        description="Crea un usuario y asígnale un rol."
      >
        <form onSubmit={handleSubmit((data) => createUser.mutate(data))} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Input id="nombre" label="Nombre" error={errors.nombre?.message} {...register("nombre")} />
            <Input id="apellidos" label="Apellidos" error={errors.apellidos?.message} {...register("apellidos")} />
          </div>
          <Input id="email" type="email" label="Email" error={errors.email?.message} {...register("email")} />
          <PasswordInput id="password" label="Contraseña" error={errors.password?.message} {...register("password")} />

          {isSuperAdmin && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Rol</label>
              <select className={selectClass} {...register("role")}>
                <option value="CLUB">Usuario de club</option>
                <option value="PROVIDER">Proveedor</option>
                <option value="REFEREE">Árbitro</option>
                <option value="SUPER_ADMIN">Super-admin</option>
              </select>
            </div>
          )}

          {((isSuperAdmin && role === "CLUB") || isProvider) && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Club</label>
              <select className={selectClass} {...register("clubId", { valueAsNumber: true })}>
                <option value="" disabled>
                  Selecciona un club
                </option>
                {clubs?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {showPermissions && (
            <div className="flex flex-col gap-2 rounded-xl bg-ink-50/60 p-4">
              <span className="text-sm font-medium text-ink-600">Permisos del usuario de club</span>
              <div className="flex flex-wrap gap-4">
                {permissions?.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm text-ink-600">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-ink-200 text-brand-600 focus:ring-brand-500/30"
                      checked={selectedPermissions.includes(p.key)}
                      onChange={() => togglePermission(p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              Crear usuario
            </Button>
          </div>
        </form>
      </Modal>

      {confirmUser && (
        <ConfirmDialog
          open={!!confirmUser}
          onOpenChange={(open) => !open && setConfirmUser(null)}
          title={confirmUser.active ? "Desactivar usuario" : "Activar usuario"}
          description={`¿Seguro que quieres ${confirmUser.active ? "desactivar" : "activar"} a "${confirmUser.nombre}"?`}
          tone="primary"
          confirmLabel={confirmUser.active ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActivo.mutate({ id: confirmUser.id, active: !confirmUser.active })}
        />
      )}
    </div>
  );
}
